import React, { useEffect, useState } from "react";
import HomePagePopup from "@/components/common/HomePagePopup";
import { api, fileUrl } from "@/lib/api";
import { Globe, TrendingUp, Handshake, Users, HandHeart } from "lucide-react";

/**
 * HomePage2 — Alternative home page built strictly from "ALT METİNLER2.docx"
 * content. Fresh minimal editorial layout distinct from the main HomePage.
 * Available at /anasayfa2 for A/B comparison.
 */

const activityAreas = [
  {
    title: "Sanayi, Teknoloji ve Savunma / Ekonomi ve Ticaret",
    body: "USEKO; sanayi, teknoloji, savunma, ekonomi ve ticaret alanlarında uluslararası iş birliklerini geliştiren, yatırım ortamını güçlendiren ve sürdürülebilir ekonomik kalkınmayı destekleyen faaliyetler yürütmektedir.",
    email: "vicepresident@useko.org",
    subs: ["İnşaat ve Altyapı", "Enerji ve Madencilik", "Savunma ve Havacılık", "Teknoloji ve Dijital Dönüşüm", "AR-GE ve İnovasyon", "Tarım, Gıda ve Su", "Lojistik ve Ulaşım"],
  },
  {
    title: "Sosyal Kalkınma",
    body: "USEKO; toplumsal refahı artıran, fırsat eşitliğini destekleyen ve sürdürülebilir sosyal gelişime katkı sağlayan proje ve programlar geliştirmektedir.",
    email: "secretarygeneral@useko.org",
    subs: ["Turizm", "Sağlık", "Sosyal Yardımlaşma ve Dayanışma", "İnsan Kaynakları", "Tekstil, Moda ve Mobilya Dekorasyon"],
  },
  {
    title: "Eğitim ve Akademik Faaliyetler",
    body: "USEKO; üniversiteler, araştırma kurumları ve eğitim kuruluşları arasında bilgi paylaşımını, akademik iş birliklerini ve nitelikli insan kaynağının gelişimini desteklemektedir.",
    email: "vicechair@useko.org",
    subs: ["Proje Geliştirme", "Kaynak Geliştirme ve Fon Yönetimi"],
  },
  {
    title: "Kültürel Diplomasi",
    body: "USEKO; kültürün evrensel birleştirici gücünden yararlanarak toplumlar arasında karşılıklı anlayışı, güveni ve uluslararası dostluğu güçlendiren faaliyetler gerçekleştirmektedir.",
    email: "deputypresident@useko.org",
    subs: ["Dijital Dönüşüm", "Kurumsal İletişim", "Uluslararası İlişkiler ve Lobicilik", "Proje ve Organizasyon"],
  },
  {
    title: "Araştırma ve Yayıncılık",
    body: "USEKO; ekonomik kalkınma, uluslararası iş birliği ve sürdürülebilir gelişim alanlarında araştırmalar yapmakta; raporlar, analizler ve yayınlarla bilgi üretimine katkı sağlamaktadır.",
    email: "finance@useko.org",
    subs: ["Strateji ve Planlama", "Hukuk ve Mevzuat", "Mali İşler"],
  },
  {
    title: "Mali ve Ticari Faaliyetler",
    body: "USEKO; kurumsal sürdürülebilirliği destekleyen mali ve ticari faaliyetleri yürütmekte; proje geliştirme, organizasyon yönetimi ve stratejik iş birlikleri yoluyla ekonomik değer üretimine katkı sunmaktadır.",
    email: "boardmember@useko.org",
    subs: ["Finans ve Yatırım Başkanlığı"],
  },
];

const programs = [
  { title: "Ticaret Heyetleri", body: "USEKO; uluslararası ticaretin geliştirilmesi amacıyla sektörel ticaret heyetleri, iş forumları, yatırım buluşmaları ve B2B görüşmeleri organize etmektedir." },
  { title: "Akademik İş Birlikleri", body: "USEKO; üniversiteler, araştırma merkezleri ve düşünce kuruluşları arasında ortak bilimsel çalışmaların ve uluslararası akademik iş birliklerinin geliştirilmesini desteklemektedir." },
  { title: "Gençlik Programları", body: "USEKO; gençlerin liderlik, girişimcilik, uluslararası deneyim ve sosyal sorumluluk alanlarında gelişimini destekleyen programlar yürütmektedir." },
  { title: "Kadın Girişimciliği Programı", body: "USEKO; kadın girişimcilerin ekonomik hayata katılımını güçlendiren, iş geliştirme süreçlerini destekleyen ve uluslararası iş ağlarına erişimlerini artıran programlar geliştirmektedir." },
  { title: "Gönüllülük Programı", body: "USEKO; küresel kalkınma, sosyal sorumluluk ve toplumsal fayda projelerinde aktif rol almak isteyen bireyleri bir araya getiren uluslararası bir gönüllülük ağı oluşturmaktadır. Program; farklı coğrafyalarda gerçekleştirilen kalkınma, eğitim, kültür ve sosyal etki projelerine nitelikli insan kaynağı desteği sağlamakta ve küresel vatandaşlık bilincini güçlendirmektedir." },
  { title: "Liderlik Programı", body: "USEKO; kamu, özel sektör, akademi ve sivil toplum alanlarında geleceğin liderlerini yetiştirmeyi hedefleyen eğitim, mentorluk ve kapasite geliştirme programları düzenlemektedir." },
  { title: "Sosyal Etki Programları", body: "USEKO; ekonomik ve sosyal kalkınmaya katkı sağlayan, ölçülebilir, sürdürülebilir ve uzun vadeli toplumsal fayda oluşturan projeler geliştirmektedir." },
];

function SectionTitle({ title, subtitle }) {
  return (
    <div className="max-w-3xl mb-12">
      <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#0B1837] tracking-tight leading-[1.05] mb-4">{title}</h2>
      {subtitle && <p className="text-lg text-[#4B5563] leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export default function HomePage2() {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get(`/settings?_=${Date.now()}`).then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);
  const heroImage = settings.hero_image ? fileUrl(settings.hero_image) : "/hero-logo.png?v=7";

  return (
    <div data-testid="home-page-2" className="home-light">
      <HomePagePopup/>

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-10 pb-10"
        style={{ backgroundImage: "url('/hero-bg.png?v=2')", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="v2-hero"
      >
        <div className="useko-container relative z-10 flex flex-col items-center text-center">
          <img src={heroImage} alt="USEKO" className="max-w-full h-auto max-h-[440px] md:max-h-[560px] lg:max-h-[640px] object-contain" style={{ filter: "drop-shadow(0 20px 60px rgba(212, 169, 79, 0.4))" }} />
        </div>
      </section>

      {/* Tagline strip */}
      <div className="bg-white py-14 border-b border-[#E5E7EB]">
        <div className="text-center px-2 sm:px-6">
          <p className="font-heading text-[13px] sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0B1837] max-w-4xl mx-auto leading-snug tracking-tight">
            &ldquo;Sürdürülebilir kalkınma, ekonomik diplomasi,<br/>
            insani dayanışma ve uluslararası iş birliğinde<br/>
            küresel strateji platformu&rdquo;
          </p>
        </div>
      </div>

      {/* Value strip — 5 icons */}
      <section className="py-16 md:py-20 bg-[#F7F7F5]" data-testid="v2-value-strip">
        <div className="useko-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {[
            { Icon: Globe, title: "KÜRESEL İŞ BİRLİĞİ", desc: "Uluslararası iş birlikleri ve ortaklık ağları kurar." },
            { Icon: TrendingUp, title: "EKONOMİK KALKINMA", desc: "Sürdürülebilir ekonomik büyümeyi ve yatırımları destekler." },
            { Icon: Handshake, title: "TİCARET VE YATIRIM", desc: "Ticaret hacmini artırmak için platformlar oluşturur." },
            { Icon: Users, title: "ORGANİZASYONEL GÜÇ", desc: "Ulusal ve uluslararası etkinlik ve organizasyonlar düzenler." },
            { Icon: HandHeart, title: "YARDIMLAŞMA VE DAYANIŞMA", desc: "İnsani yardım ve sosyal projelerle toplumsal dayanışmayı güçlendirir." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="text-center flex flex-col items-center">
              <Icon className="w-12 h-12 text-[#B8862F] mb-6" strokeWidth={1.3}/>
              <h3 className="font-semibold tracking-[0.14em] text-sm text-[#0B1837] mb-3 leading-tight">{title}</h3>
              <p className="text-sm text-[#4B5563] leading-relaxed max-w-[220px]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* USEKO Nedir? & Neden USEKO? — Split editorial */}
      <section className="py-24 bg-white" data-testid="v2-what-why">
        <div className="useko-container grid lg:grid-cols-2 gap-16">
          <div>
            <div className="w-full aspect-[16/7] overflow-hidden rounded-2xl mb-8 border border-[#E5E7EB]">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/obwa8hm3_NedenUseko.png"
                alt="USEKO Nedir görseli"
                loading="lazy"
                className="w-full h-full object-cover"
                data-testid="v2-what-image"
              />
            </div>
            <div className="w-12 h-1 bg-[#D4A94F] mb-6"/>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#0B1837] mb-6 tracking-tight leading-[1.05]">USEKO Nedir?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">USEKO; ekonomik kalkınma, ticari diplomasi ve uluslararası iş birliği alanlarında faaliyet gösteren, Türkiye merkezli uluslararası bir sivil toplum kuruluşudur. Kamu, özel sektör, akademi ve uluslararası kuruluşlar arasında stratejik ortaklıklar geliştirerek sürdürülebilir kalkınmaya ve küresel iş birliğine katkı sunmayı amaçlamaktadır.</p>
          </div>
          <div>
            <div className="w-full aspect-[16/7] overflow-hidden rounded-2xl mb-8 border border-[#E5E7EB]">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/c759p2i8_USEKONedir.png"
                alt="Neden USEKO görseli"
                loading="lazy"
                className="w-full h-full object-cover"
                data-testid="v2-why-image"
              />
            </div>
            <div className="w-12 h-1 bg-[#050C2D] mb-6"/>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#0B1837] mb-6 tracking-tight leading-[1.05]">Neden USEKO?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">Küresel ölçekte sürdürülebilir kalkınma; güçlü ortaklıklar, güvene dayalı iş birlikleri ve ortak akıl gerektirmektedir. USEKO, farklı sektörleri ve ülkeleri ortak hedefler etrafında buluşturarak ekonomik, sosyal ve kültürel değer üreten sürdürülebilir iş birliği modelleri geliştirmektedir.</p>
          </div>
        </div>
      </section>

      {/* Hakkımızda — full-width statement */}
      <section className="py-24 bg-[#0B1837] text-[#EDE4CE]" data-testid="v2-about">
        <div className="useko-container max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-[#EDE4CE] mb-6">Hakkımızda</h2>
          <p className="font-heading text-2xl md:text-3xl leading-relaxed text-[#EDE4CE]">
            USEKO; ekonomik kalkınma, ticaret, yatırım, eğitim, kültürel diplomasi ve uluslararası organizasyonlar alanında faaliyet gösteren, çok paydaşlı iş birliği modellerini destekleyen uluslararası vizyona sahip bir sivil toplum kuruluşudur. Gerçekleştirdiği çalışmalarla ülkeler, kurumlar ve toplumlar arasında kalıcı değer üretmeyi hedeflemektedir.
          </p>
        </div>
      </section>

      {/* Misyon & Vizyon */}
      <section className="py-24 bg-[#FBFAF6]" data-testid="v2-mission-vision">
        <div className="useko-container grid md:grid-cols-2 gap-8">
          <div className="p-10 bg-white rounded-2xl border-t-4 border-[#D4A94F] shadow-sm">
            <h3 className="font-heading text-3xl font-bold text-[#0B1837] mb-4">Misyon</h3>
            <p className="text-[#4B5563] leading-relaxed">Ulusal ve uluslararası düzeyde ekonomik kalkınmayı destekleyen, ticari ve stratejik iş birliklerini güçlendiren, bilgi ve deneyim paylaşımını teşvik eden, sürdürülebilir ve kapsayıcı ortaklık modelleri geliştirmek.</p>
          </div>
          <div className="p-10 bg-white rounded-2xl border-t-4 border-[#050C2D] shadow-sm">
            <h3 className="font-heading text-3xl font-bold text-[#0B1837] mb-4">Vizyon</h3>
            <p className="text-[#4B5563] leading-relaxed">Türkiye'nin ekonomik, ticari ve diplomatik kapasitesinin uluslararası ölçekte daha güçlü temsil edilmesine katkı sağlayan, güvenilir ve saygın bir küresel iş birliği platformu olmak.</p>
          </div>
        </div>
      </section>

      {/* Temel İlkeler */}
      <section className="py-24 bg-white" data-testid="v2-principles">
        <div className="useko-container">
          <SectionTitle title="Temel İlkeler" />
          <p className="text-lg text-[#4B5563] leading-relaxed max-w-3xl">USEKO; sürdürülebilirlik, güven, şeffaflık, etik yönetim, katılımcılık, hesap verebilirlik, kapsayıcılık, uluslararası iş birliği, toplumsal fayda ve ortak değer üretimi ilkeleri doğrultusunda faaliyet göstermektedir.</p>
        </div>
      </section>

      {/* Organizasyon Yapısı */}
      <section className="py-24 bg-white" data-testid="v2-organization">
        <div className="useko-container">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#0B1837] tracking-tight leading-[1.05] mb-4">Organizasyon Yapısı</h2>
          <div className="w-16 h-1 bg-[#D4A94F] mb-12"/>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Sektörel */}
            <div className="p-8 md:p-10 bg-[#FBFAF6] border border-[#E5E7EB] rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#D4A94F] flex items-center justify-center text-[#0B1837] font-bold text-lg">S</div>
                <h3 className="font-heading text-2xl font-bold text-[#0B1837]">Sektörel Başkanlıklar</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#374151]">
                {["İnşaat ve Altyapı","Enerji ve Madencilik","Savunma ve Havacılık","Teknoloji ve Dijital Dönüşüm","Tarım, Gıda ve Su","Sağlık","Turizm","Tekstil, Moda ve Mobilya Dekorasyon","Finans ve Yatırım","Lojistik ve Ulaştırma"].map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="text-[#B8862F] font-semibold text-xs mt-0.5 min-w-[20px]">{String(i+1).padStart(2,'0')}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Fonksiyonel */}
            <div className="p-8 md:p-10 bg-[#FBFAF6] border border-[#E5E7EB] rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#0B1837] flex items-center justify-center text-[#EDE4CE] font-bold text-lg">F</div>
                <h3 className="font-heading text-2xl font-bold text-[#0B1837]">Fonksiyonel Başkanlıklar</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#374151]">
                {["Hukuk ve Mevzuat","Mali İşler","İnsan Kaynakları","Kurumsal İletişim","Uluslararası İlişkiler ve Lobicilik","AR-GE ve İnovasyon","Dijital Dönüşüm","Strateji ve Planlama","Proje Geliştirme","Proje ve Organizasyon","Kaynak Geliştirme ve Fon Yönetimi","Sosyal Yardımlaşma ve Dayanışma"].map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="text-[#B8862F] font-semibold text-xs mt-0.5 min-w-[20px]">{String(i+1).padStart(2,'0')}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Faaliyet Alanları */}
      <section className="py-24 bg-[#FBFAF6]" data-testid="v2-activities">
        <div className="useko-container">
          <SectionTitle title="Faaliyet Alanları" />
          <div className="grid md:grid-cols-2 gap-6">
            {activityAreas.map((a) => (
              <article key={a.title} className="p-8 bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#D4A94F]/50 hover:shadow-lg transition-all flex flex-col">
                <h3 className="font-heading text-xl font-bold text-[#0B1837] mb-3 leading-snug">{a.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed mb-5">{a.body}</p>
                {a.subs?.length > 0 && (
                  <div className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#B8862F] font-semibold mb-2">Bölüm Faaliyetleri</div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#374151]">
                      {a.subs.map((s) => (
                        <li key={s} className="flex items-start gap-2">
                          <span className="text-[#D4A94F] mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-auto pt-4 border-t border-[#E5E7EB]">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#4B5563] font-semibold mb-1.5">E-Posta</div>
                  <a href={`mailto:${a.email}`} className="inline-block px-4 py-2 rounded-full bg-gradient-to-br from-[#E8C577] via-[#D4A94F] to-[#B8862F] text-[#051436] font-bold text-xs" data-testid={`v2-email-${a.email}`}>
                    {a.email}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Uluslararası İş Birliği Programları + alt programlar */}
      <section className="py-24 bg-white" data-testid="v2-programs">
        <div className="useko-container">
          <SectionTitle title="Uluslararası İş Birliği Programları" subtitle="USEKO; ülkeler, kurumlar ve uluslararası kuruluşlar arasında sürdürülebilir ortaklıklar geliştiren, bilgi paylaşımını ve ortak proje üretimini destekleyen programlar yürütmektedir." />
          <div className="grid md:grid-cols-2 gap-6">
            {programs.map((p) => (
              <article key={p.title} className="group relative p-8 bg-[#FBFAF6] rounded-2xl border border-[#E5E7EB] hover:bg-white hover:border-[#0B1837]/20 hover:shadow-lg transition-all">
                <h3 className="font-heading text-xl font-bold text-[#0B1837] mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
